/**
 * Settings → Add to home screen. Lets a caregiver install the app as a PWA so it
 * opens like a native app (standalone, its own icon) instead of a browser tab.
 *
 * We ship no service worker (see web/app/CLAUDE.md), so most browsers won't fire
 * `beforeinstallprompt` and install is a browser-menu action — hence this leads
 * with per-platform guided steps. Where a browser *does* offer the event
 * (captured in lib/pwaInstall.ts), we show a real one-tap button instead.
 *
 * Owns its own Card so the whole section disappears once the app is already
 * running installed (standalone) — there's nothing to offer then.
 */

import { useEffect, useState } from 'react';

import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  canPromptInstall,
  detectPlatform,
  isStandalone,
  onInstallAvailabilityChange,
  promptInstall,
  type InstallPlatform,
} from '@/lib/pwaInstall';

const STEPS: Record<InstallPlatform, { intro: string; steps: React.ReactNode[] }> = {
  ios: {
    intro: 'In Safari, add RemindMeVoice to your home screen:',
    steps: [
      <>
        Tap the <span className="font-medium">Share</span> button (the square with an
        upward arrow) at the bottom of the screen.
      </>,
      <>
        Scroll down and tap <span className="font-medium">Add to Home Screen</span>.
      </>,
      <>
        Tap <span className="font-medium">Add</span> — the RemindMeVoice icon appears on
        your home screen.
      </>,
    ],
  },
  android: {
    intro: 'Add RemindMeVoice to your home screen:',
    steps: [
      <>
        Tap the <span className="font-medium">⋮ menu</span> in the top-right of your
        browser.
      </>,
      <>
        Tap <span className="font-medium">Install app</span> (or{' '}
        <span className="font-medium">Add to Home screen</span>).
      </>,
      <>Confirm — the RemindMeVoice icon appears on your home screen.</>,
    ],
  },
  desktop: {
    intro: 'Install RemindMeVoice as an app:',
    steps: [
      <>
        Click the <span className="font-medium">install icon</span> in your browser's
        address bar — or open the <span className="font-medium">⋮ menu</span> and choose{' '}
        <span className="font-medium">Install RemindMeVoice</span>.
      </>,
      <>Confirm to add it to your applications.</>,
    ],
  },
};

export function InstallAppSection() {
  // Platform + standalone are fixed for the session; availability of the one-tap
  // prompt can change as the browser decides the app is installable.
  const [standalone] = useState(isStandalone);
  const [platform] = useState<InstallPlatform>(detectPlatform);
  const [canPrompt, setCanPrompt] = useState(canPromptInstall);
  const [justInstalled, setJustInstalled] = useState(false);

  useEffect(() => onInstallAvailabilityChange(() => setCanPrompt(canPromptInstall())), []);

  if (standalone) return null; // already installed — nothing to offer

  async function onInstall() {
    const outcome = await promptInstall();
    if (outcome === 'accepted') setJustInstalled(true);
  }

  const guide = STEPS[platform];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add to home screen</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="text-sm text-ink-soft">
          Add RemindMeVoice to your phone or computer so it opens like an app — its own
          icon, no browser bar.
        </p>

        {justInstalled ? (
          <p className="mt-4 rounded-md bg-accent-50 px-3 py-2 text-sm text-ink">
            ✓ Added — look for the RemindMeVoice icon on your home screen.
          </p>
        ) : canPrompt ? (
          <div className="mt-4">
            <Button type="button" onClick={onInstall}>
              Add to home screen
            </Button>
          </div>
        ) : (
          <div className="mt-4">
            <p className="text-sm text-ink-soft">{guide.intro}</p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-ink-soft">
              {guide.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
